<template>
  <div class="template-shell">
    <div v-if="homePage" class="home-container">
      <component
        :is="dynamicComponent"
        :homeImage="homeImage"
        :exhibitIdProps="id"
        @exit="exitHomePage"
      />
    </div>

    <main
      v-else
      class="immersive-slider"
      @pointerdown="handlePointerDown"
      @pointerup="handlePointerUp"
      @pointercancel="resetPointer"
    >
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource dans la galerie</div>

      <template v-else>
        <transition name="slide-fade" mode="out-in">
          <section :key="activeIndex" class="slide">
            <img
              v-if="isImageResource(activeResource) && activeUrl"
              :src="activeUrl"
              :alt="activeResource.title || exhibitName"
            />
            <video v-else-if="getResourceType(activeResource) === 'video' && activeUrl" :src="activeUrl" autoplay muted loop playsinline />
            <div v-else class="file-slide">
              <q-icon :name="fileIcon(activeResource)" />
              <span>{{ activeUrl === null ? 'Media indisponible' : activeResource.title || activeResource.name || 'Chargement...' }}</span>
            </div>
          </section>
        </transition>

        <div class="slide-overlay"></div>

        <section class="slide-copy">
          <h1>{{ activeResource.title || exhibitName }}</h1>
          <p>{{ activeResource.description || exhibitName }}</p>
        </section>

        <button class="nav-zone nav-zone--prev" type="button" @click="previous" aria-label="Image précédente">
          <q-icon name="chevron_left" />
        </button>
        <button class="nav-zone nav-zone--next" type="button" @click="next" aria-label="Image suivante">
          <q-icon name="chevron_right" />
        </button>

        <div class="dots" aria-label="Navigation des images">
          <button
            v-for="(_, index) in resources"
            :key="index"
            type="button"
            :class="{ active: index === activeIndex }"
            @click="goTo(index)"
            :aria-label="`Image ${index + 1}`"
          ></button>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue';
import { useGalleryTemplate } from '../../composables/useGalleryTemplate';

const props = defineProps({
  homePageProps: { type: Boolean, default: true },
  exhibitId: { type: String, default: null },
});

const {
  dynamicComponent,
  exhibitName,
  exitHomePage,
  getResourceType,
  homeImage,
  homePage,
  id,
  isImageResource,
  loading,
  resourceUrls,
  resources,
} = useGalleryTemplate(props);

const activeIndex = ref(0);
const pointerStartX = ref(null);

const activeResource = computed(() => resources.value[activeIndex.value] || {});
const activeUrl = computed(() => resourceUrls.value[activeIndex.value] || '');

const goTo = (index) => {
  if (!resources.value.length) return;
  activeIndex.value = (index + resources.value.length) % resources.value.length;
};

const next = () => goTo(activeIndex.value + 1);
const previous = () => goTo(activeIndex.value - 1);

const handlePointerDown = (event) => {
  pointerStartX.value = event.clientX;
};

const handlePointerUp = (event) => {
  if (pointerStartX.value === null) return;
  const delta = event.clientX - pointerStartX.value;
  if (Math.abs(delta) > 48) {
    delta < 0 ? next() : previous();
  }
  resetPointer();
};

const resetPointer = () => {
  pointerStartX.value = null;
};

const fileIcon = (resource) => {
  const type = getResourceType(resource);
  if (type === 'audio') return 'audiotrack';
  if (type === 'pdf' || type === 'doc') return 'description';
  return 'insert_drive_file';
};
</script>

<style scoped>
.template-shell {
  min-height: 100vh;
  background: #000;
  color: #fff;
}

.immersive-slider {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #050505;
  touch-action: pan-y;
}

.slide {
  position: absolute;
  inset: 0;
}

.slide img,
.slide video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  animation: slowScale 8s ease forwards;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(to top, rgba(0, 0, 0, 0.48), rgba(0, 0, 0, 0.04) 52%, rgba(0, 0, 0, 0.12)),
    linear-gradient(to right, rgba(0, 0, 0, 0.34), rgba(0, 0, 0, 0) 46%);
}

.slide-copy {
  position: absolute;
  left: 64px;
  bottom: 76px;
  z-index: 2;
  max-width: 560px;
  animation: copyIn 520ms ease both;
}

h1 {
  margin: 0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 64px;
  font-weight: 400;
  line-height: 1.04;
}

.slide-copy p:not(.eyebrow) {
  max-width: 460px;
  margin: 14px 0 0;
  color: #f3f3f3;
  font-size: 18px;
  line-height: 1.45;
}

.nav-zone {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 18vw;
  min-width: 90px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0);
  cursor: pointer;
  font-size: 58px;
  transition: color 160ms ease, background 160ms ease;
}

.nav-zone:hover {
  color: rgba(255, 255, 255, 0.82);
  background: rgba(0, 0, 0, 0.12);
}

.nav-zone:active {
  color: #fff;
}

.nav-zone--prev {
  left: 0;
}

.nav-zone--next {
  right: 0;
}

.dots {
  position: absolute;
  left: 50%;
  bottom: 28px;
  z-index: 4;
  display: flex;
  gap: 16px;
  transform: translateX(-50%);
}

.dots button {
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  opacity: 0.92;
  transition: transform 160ms ease, background 160ms ease, opacity 160ms ease;
}

.dots button.active {
  background: #d9b372;
  transform: scale(1.22);
  opacity: 1;
}

.state-panel {
  min-height: 100vh;
  display: grid;
  place-items: center;
  color: #dedede;
}

.file-slide {
  display: grid;
  place-items: center;
  gap: 18px;
  height: 100vh;
  padding: 42px;
  text-align: center;
  background: linear-gradient(135deg, #171717, #050505);
}

.file-slide .q-icon {
  font-size: 80px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: scale(1.015);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.99);
}

@keyframes slowScale {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.04);
  }
}

@keyframes copyIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 780px) {
  .slide-copy {
    left: 24px;
    right: 24px;
    bottom: 72px;
    max-width: none;
  }

  h1 {
    font-size: 42px;
  }

  .nav-zone {
    width: 50%;
    min-width: 0;
    color: rgba(255, 255, 255, 0);
  }

  .dots {
    gap: 12px;
  }

  .dots button {
    width: 14px;
    height: 14px;
  }
}
</style>
